import { GraphQLClient as GraphQLRequestClient } from 'graphql-request'
import { getAuthTokens } from '@vacationtracker/shared/services/auth'
import { NoTokens } from '@vacationtracker/shared/errors/auth'

const GraphQlApiUrl = process.env.REACT_APP_GRAPHQL_ENDPOINT || ''

/**
 * GraphQL client using graphql-request for TanStack Query integration
 *
 * Features:
 * - Automatic authentication token injection
 * - 401 error handling with token refresh
 * - Compatible with TanStack Query
 */
class VacationTrackerGraphQLClient {
  private client: GraphQLRequestClient
  private isRefreshing = false
  private refreshSubscribers: Array<(token: string) => void> = []

  constructor() {
    this.client = new GraphQLRequestClient(GraphQlApiUrl, {
      // Custom fetch implementation to handle 401 errors
      fetch: async (input, init) => {
        const response = await fetch(input, init)

        // Handle 401 Unauthorized - refresh token and retry
        if (response.status === 401) {
          try {
            // If already refreshing, wait for the new token
            if (this.isRefreshing) {
              const newToken = await new Promise<string>((resolve) => {
                this.refreshSubscribers.push(resolve)
              })

              // Retry the request with the new token
              if (init?.headers) {
                const headers = init.headers as Record<string, string>
                headers['Authorization'] = newToken
              }
              return fetch(input, init)
            }

            // Start token refresh
            this.isRefreshing = true

            // Force refresh the token
            const { idToken } = await getAuthTokens(true)

            // Update the request headers with new token
            if (init?.headers) {
              const headers = init.headers as Record<string, string>
              headers['Authorization'] = idToken
            }

            // Notify all subscribers waiting for the new token
            this.refreshSubscribers.forEach((callback) => callback(idToken))
            this.refreshSubscribers = []

            this.isRefreshing = false

            // Retry the request with the new token
            return fetch(input, init)
          } catch (error) {
            this.isRefreshing = false
            this.refreshSubscribers = []
            throw error
          }
        }

        return response
      },
    })
  }

  /**
   * Update the Authorization header with the current JWT token
   */
  async updateAuthHeader(): Promise<void> {
    try {
      const { idToken } = await getAuthTokens()
      this.client.setHeader('Authorization', idToken)
    } catch (error) {
      if (error.name === NoTokens.code) {
        // Clear the Authorization header if no tokens are available
        this.client.setHeader('Authorization', '')
        throw error
      }
      throw error
    }
  }

  /**
   * Make a GraphQL request with automatic authentication
   */
  async request<T = unknown>(document: string, variables?: Record<string, unknown>): Promise<T> {
    // Ensure we have the auth header before making the request
    await this.updateAuthHeader()
    return this.client.request<T>(document, variables)
  }

  /**
   * Get the underlying GraphQL client (for advanced use cases)
   */
  getClient(): GraphQLRequestClient {
    return this.client
  }
}

/**
 * Singleton instance of the GraphQL client
 */
export const graphqlClient = new VacationTrackerGraphQLClient()

/**
 * Helper function to make GraphQL requests
 * This is the main function to use with TanStack Query
 */
export async function graphqlRequest<T = unknown>(document: string, variables?: Record<string, unknown>): Promise<T> {
  return graphqlClient.request<T>(document, variables)
}
